import { useRef, useState, type DragEvent, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  createAsset,
  deleteAsset,
  importAssets,
  listAssetStatuses,
  listAssetTypes,
  listAssetCategories,
  listAssets,
  listAssignees,
  updateAsset,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { PageBar } from "../components/Layout";
import { Modal } from "../components/Modal";
import { confirmDialog } from "../components/ConfirmDialog";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { SortTh, useTableSort, type SortAccessor } from "../components/SortableTable";
import { RichTextEditor, richTextIsEmpty } from "../components/RichText";
import type {
  AssetImportResult,
  AssetImportRow,
  AssetRow,
  AssetStatusRow,
  CreateAssetInput,
  UpdateAssetInput,
} from "../lib/types";

const ASSET_SORT: Record<string, SortAccessor<AssetRow>> = {
  tag: (a) => a.asset_tag,
  name: (a) => a.name,
  type: (a) => a.type_name,
  category: (a) => a.category_name,
  qty: (a) => a.quantity,
  status: (a) => a.status,
  holder: (a) => a.holder_name,
};

/** Translate a hex color into an rgba() string at the given alpha. Tolerates a
 *  missing leading "#" and 3-digit shorthand; non-hex input is returned as-is. */
function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(h)) return hex;
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** A small colored pill for an asset's status. Looks the status CODE up in the
 *  tenant-configured list and renders its name + color (soft-tinted background,
 *  colored text/border). Falls back to the raw code if the status isn't found. */
export function AssetStatusBadge({ status, statuses }: { status: string; statuses: AssetStatusRow[] }) {
  const row = statuses.find((s) => s.code === status);
  const label = row?.name ?? status;
  const color = row?.color ?? null;
  const style = color
    ? { background: hexToRgba(color, 0.14), color, border: `1px solid ${hexToRgba(color, 0.35)}` }
    : undefined;
  // Keep the .asset-status base class for shape; per-status CSS modifier stays a
  // fallback for known codes when no configured color is available.
  return (
    <span className={`asset-status asset-status-${status}`} style={style} title="Status">
      {label}
    </span>
  );
}

export function AssetsPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>("");
  const { data, loading, error, reload } = useApi(
    (signal) => listAssets(statusFilter ? { status: statusFilter } : undefined, signal),
    [statusFilter],
  );
  const statusesApi = useApi((s) => listAssetStatuses(s), []);
  const assetStatuses = statusesApi.data?.asset_statuses ?? [];
  const categoriesApi = useApi((s) => listAssetCategories(s), []);
  const categories = categoriesApi.data?.asset_categories ?? [];
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [query, setQuery] = useState<string>("");
  const allAssets = data?.assets ?? [];
  // Category + free-text filters are applied client-side over the loaded list
  // (no extra round-trip). Search spans tag, name, type, category, holder,
  // and serial.
  const q = query.trim().toLowerCase();
  const assets = allAssets.filter((a) => {
    if (categoryFilter && a.category_id !== categoryFilter) return false;
    if (!q) return true;
    return [a.asset_tag, a.name, a.type_name, a.category_name, a.holder_name, a.serial].some(
      (v) => (v ?? "").toLowerCase().includes(q),
    );
  });
  const { rows: sortedAssets, sort, toggle } = useTableSort(assets, ASSET_SORT);
  // null = closed, "new" = create, row = edit
  const [editing, setEditing] = useState<AssetRow | "new" | null>(null);
  const [importing, setImporting] = useState(false);

  return (
    <>
      <PageBar
        crumbs="Inventory"
        actions={
          <>
            <button className="btn" onClick={() => setImporting(true)}>
              Import
            </button>
            <button className="btn primary" onClick={() => setEditing("new")}>
              + New asset
            </button>
          </>
        }
      />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Inventory</h1>
            <div className="sub">All assets in stock or in someone's possession.</div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>Assets</h3>
            <div className="btn-row" style={{ alignItems: "center" }}>
              <input
                type="search"
                className="input-inline"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search assets…"
                aria-label="Search assets"
                style={{ minWidth: 220 }}
              />
              <select
                className="input-inline"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="">All statuses</option>
                {assetStatuses.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
              <select
                className="input-inline"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                aria-label="Filter by category"
              >
                <option value="">All categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <span className="faint">{assets.length}</span>
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <Loading label="Loading assets…" />
            ) : error ? (
              <ErrorState error={error} onRetry={reload} />
            ) : assets.length === 0 ? (
              <EmptyState>No assets yet. Add one, then check it out to a person.</EmptyState>
            ) : (
              <table className="data">
                <thead>
                  <tr>
                    <SortTh field="tag" sort={sort} onSort={toggle}>Tag</SortTh>
                    <SortTh field="name" sort={sort} onSort={toggle}>Name</SortTh>
                    <SortTh field="type" sort={sort} onSort={toggle}>Type</SortTh>
                    <SortTh field="category" sort={sort} onSort={toggle}>Category</SortTh>
                    <SortTh field="qty" sort={sort} onSort={toggle}>Qty</SortTh>
                    <SortTh field="status" sort={sort} onSort={toggle}>Status</SortTh>
                    <SortTh field="holder" sort={sort} onSort={toggle}>Holder</SortTh>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {sortedAssets.map((a) => (
                    <AssetTr
                      key={a.id}
                      asset={a}
                      statuses={assetStatuses}
                      onOpen={() => navigate(`/assets/${a.id}`)}
                      onEdit={() => setEditing(a)}
                      onChanged={reload}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {editing ? (
        <AssetModal
          asset={editing === "new" ? null : editing}
          statuses={assetStatuses}
          onClose={() => setEditing(null)}
          onSaved={(createdId) => {
            setEditing(null);
            reload();
            if (createdId) navigate(`/assets/${createdId}`);
          }}
        />
      ) : null}

      {importing ? (
        <AssetImportModal
          onClose={() => setImporting(false)}
          onImported={() => {
            setImporting(false);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Minimal RFC-4180-ish CSV parser: handles quoted fields, escaped "" quotes,
 *  and CR/LF line breaks. Returns rows of string cells. */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      // Close the row on a newline; swallow the \n of a \r\n pair.
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cell);
      cell = "";
      rows.push(row);
      row = [];
    } else {
      cell += ch;
    }
  }
  // Flush a trailing cell/row if the file didn't end with a newline.
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}

/** Map a header cell to one of our import fields (tolerant aliases). */
const HEADER_ALIASES: Record<string, keyof AssetImportRow> = {
  name: "name",
  "asset name": "name",
  tag: "asset_tag",
  "asset tag": "asset_tag",
  asset_tag: "asset_tag",
  type: "type",
  category: "type",
  serial: "serial",
  "serial number": "serial",
  status: "status",
  holder: "holder",
  "assigned to": "holder",
  assignee: "holder",
  "holder email": "holder",
  notes: "notes",
  note: "notes",
  quantity: "quantity",
  qty: "quantity",
  count: "quantity",
};

const IMPORT_TEMPLATE = "name,asset_tag,type,serial,status,holder,notes,quantity";

function AssetImportModal({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<AssetImportResult | null>(null);
  const { run, pending, error } = useMutation((rows: AssetImportRow[]) => importAssets(rows));

  // Parse on the fly so we can show a row count + guard empty submits.
  const parsed = text.trim() ? parseCsv(text) : [];
  const hasHeader = parsed.length > 0;
  const dataRowCount = hasHeader ? parsed.length - 1 : 0;

  function readFile(file: File) {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setText(typeof reader.result === "string" ? reader.result : "");
    reader.readAsText(file);
  }

  /** Accept the first CSV-ish file from a drop or picker (ignore the rest). */
  function takeFiles(files: FileList | null | undefined) {
    const file = files?.[0];
    if (file) readFile(file);
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    takeFiles(e.dataTransfer.files);
  }

  function toRows(): AssetImportRow[] {
    if (parsed.length < 2) return [];
    const header = parsed[0].map((h) => HEADER_ALIASES[h.trim().toLowerCase()]);
    return parsed.slice(1).map((cells) => {
      const row: AssetImportRow = {};
      header.forEach((field, idx) => {
        if (field && cells[idx] != null) row[field] = cells[idx].trim();
      });
      return row;
    });
  }

  async function onSubmit() {
    const rows = toRows();
    if (!rows.length) return;
    const res = await run(rows);
    if (res) {
      setResult(res);
      if (res.created > 0) onImported(); // refresh the list behind the modal
    }
  }

  return (
    <Modal
      title="Import assets"
      onClose={onClose}
      footer={
        result ? (
          <button className="btn primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button className="btn" onClick={onClose} disabled={pending}>
              Cancel
            </button>
            <button className="btn primary" onClick={onSubmit} disabled={pending || dataRowCount === 0}>
              {pending ? <Spinner label="Importing…" /> : `Import ${dataRowCount || ""}`.trim()}
            </button>
          </>
        )
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}

      {result ? (
        <div>
          <p>
            <strong>{result.created}</strong> asset{result.created === 1 ? "" : "s"} imported
            {result.errors.length ? `, ${result.errors.length} skipped` : ""}.
          </p>
          {result.errors.length ? (
            <ul className="import-errors">
              {result.errors.map((e) => (
                <li key={e.line}>
                  Row {e.line}: {e.message}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : (
        <>
          <p className="hint" style={{ marginTop: 0 }}>
            Upload or paste CSV. First row is the header. Recognized columns:{" "}
            <code>name, asset_tag, type, serial, status, holder, notes, quantity</code>. Only{" "}
            <code>name</code> is required. Unknown <code>type</code> values are created automatically;{" "}
            <code>holder</code> matches a user by email or name.
          </p>
          <div className="field">
            <span className="label">CSV file</span>
            <div
              className={`dropzone${dragging ? " dropzone-active" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(e) => takeFiles(e.target.files)}
              />
              {fileName ? (
                <p className="dropzone-file">
                  <strong>{fileName}</strong> — drop another file or click to replace
                </p>
              ) : (
                <p className="dropzone-hint">
                  <strong>Drag &amp; drop</strong> a CSV file here, or <u>click to browse</u>
                </p>
              )}
            </div>
          </div>
          <label className="field">
            <span className="label">…or paste CSV</span>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              placeholder={`${IMPORT_TEMPLATE}\nDell XPS 13,AC-0142,Laptop,SN123,in_stock,jane@school.edu,Main Campus,,1`}
              style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
            />
          </label>
          {hasHeader ? (
            <p className="faint">
              {dataRowCount} data row{dataRowCount === 1 ? "" : "s"} detected.
            </p>
          ) : null}
        </>
      )}
    </Modal>
  );
}

function AssetTr({
  asset,
  statuses,
  onOpen,
  onEdit,
  onChanged,
}: {
  asset: AssetRow;
  statuses: AssetStatusRow[];
  onOpen: () => void;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const deleteMut = useMutation(deleteAsset);
  const busy = deleteMut.pending;

  return (
    <tr
      className="clickable"
      onClick={onOpen}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
    >
      <td className="muted">{asset.asset_tag || "—"}</td>
      <td>
        <strong>{asset.name}</strong>
      </td>
      <td className="muted">{asset.type_name || "—"}</td>
      <td className="muted">{asset.category_name || "—"}</td>
      <td className="muted">{asset.quantity}</td>
      <td>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <AssetStatusBadge status={asset.status} statuses={statuses} />
          {asset.is_overdue ? (
            <span className="asset-status asset-status-overdue" style={{ color: "#b91c1c", borderColor: "#b91c1c" }}>
              Overdue
            </span>
          ) : null}
        </span>
      </td>
      <td>{asset.holder_name || <span className="muted">Unassigned</span>}</td>
      <td className="row-actions">
        <button
          className="btn small"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          Edit
        </button>
        <button
          className="btn small danger"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            void confirmDialog({ message: `Delete asset "${asset.name}"?`, danger: true }).then((yes) => {
              if (yes) void deleteMut.run(asset.id).then((ok) => ok && onChanged());
            });
          }}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

/** Add/edit asset modal — null asset = create. On create you may set an initial
 *  holder (logged as a check-out); on edit, possession is changed via the detail
 *  page's check-out/check-in so custody is always recorded. */
export function AssetModal({
  asset,
  statuses,
  onClose,
  onSaved,
}: {
  asset: AssetRow | null;
  statuses: AssetStatusRow[];
  onClose: () => void;
  onSaved: (createdId?: string) => void;
}) {
  const typesApi = useApi((signal) => listAssetTypes(signal), []);
  const categoriesApi = useApi((signal) => listAssetCategories(signal), []);
  const assigneesApi = useApi((signal) => listAssignees(signal), []);
  const types = typesApi.data?.asset_types ?? [];
  const categories = categoriesApi.data?.asset_categories ?? [];
  const assignees = assigneesApi.data?.assignees ?? [];

  const [name, setName] = useState(asset?.name ?? "");
  const [tag, setTag] = useState(asset?.asset_tag ?? "");
  const [typeId, setTypeId] = useState(asset?.type_id ?? "");
  const [categoryId, setCategoryId] = useState(asset?.category_id ?? "");
  const [serial, setSerial] = useState(asset?.serial ?? "");
  const [quantity, setQuantity] = useState(String(asset?.quantity ?? 1));
  // Default a NEW asset to the configured "in_stock" status, else the first one.
  const defaultStatus =
    statuses.find((s) => s.code === "in_stock")?.code ?? statuses[0]?.code ?? "in_stock";
  const [status, setStatus] = useState<string>(asset?.status ?? defaultStatus);
  const [holderId, setHolderId] = useState(asset?.holder_id ?? "");
  const [notes, setNotes] = useState(asset?.notes ?? "");
  // Two-up rows: each field takes 50% of the row width.
  const half = { flex: 1, minWidth: 0 } as const;

  const createMut = useMutation((input: CreateAssetInput) => createAsset(input));
  const updateMut = useMutation((id: string, input: UpdateAssetInput) => updateAsset(id, input));
  const pending = createMut.pending || updateMut.pending;
  const error = createMut.error || updateMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    // Clamp to a non-negative integer; blank/invalid falls back to 1.
    const qty = Math.max(0, Math.floor(Number(quantity)));
    const common = {
      name: name.trim(),
      asset_tag: tag.trim() || null,
      type_id: typeId || null,
      category_id: categoryId || null,
      serial: serial.trim() || null,
      status,
      notes: richTextIsEmpty(notes) ? null : notes,
      quantity: Number.isFinite(qty) ? qty : 1,
    };
    if (asset) {
      // Edit: holder is intentionally NOT sent here (use check-out/check-in).
      const res = await updateMut.run(asset.id, common);
      if (res) onSaved();
    } else {
      const res = await createMut.run({ ...common, holder_id: holderId || null });
      if (res) onSaved(res.asset.id);
    }
  }

  return (
    <Modal
      title={asset ? "Edit asset" : "Add asset"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button className="btn primary" type="submit" form="asset-form" disabled={pending || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : asset ? "Save changes" : "Add asset"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="asset-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Dell XPS 13" autoFocus required />
        </label>
        <div className="row">
          <label className="field" style={half}>
            <span className="label">Asset tag</span>
            <input type="text" value={tag} onChange={(e) => setTag(e.target.value)} placeholder="AC-0142" />
          </label>
          <label className="field" style={half}>
            <span className="label">Serial</span>
            <input type="text" value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="Service tag / serial" />
          </label>
        </div>
        <div className="row">
          <label className="field" style={half}>
            <span className="label">Type</span>
            <select value={typeId} onChange={(e) => setTypeId(e.target.value)}>
              <option value="">—</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field" style={half}>
            <span className="label">Category</span>
            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">—</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="row">
          <label className="field" style={half}>
            <span className="label">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {statuses.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field" style={half}>
            <span className="label">Quantity</span>
            <input
              type="number"
              min={0}
              step={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="1"
            />
          </label>
        </div>
        {!asset ? (
          <div className="row">
            <label className="field" style={half}>
              <span className="label">Assign to (optional)</span>
              <select value={holderId} onChange={(e) => setHolderId(e.target.value)}>
                <option value="">— Unassigned</option>
                {assignees.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="field" style={half} />
          </div>
        ) : null}
        <label className="field">
          <span className="label">Notes</span>
          <RichTextEditor value={notes} onChange={setNotes} placeholder="Notes…" />
        </label>
      </form>
    </Modal>
  );
}
